import { useCallback, useEffect, useMemo, useState } from 'react';
import { website as Api, type EditableField, type WebsitePage } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { SkeletonPage } from '../components/Shimmer';
import { SeoPanel } from '../components/SeoPanel';
import { usePreview } from '../lib/usePreview';
import { ImagePick } from '../components/ImagePick';

export function Pages() {
  const { data, error, loading, reload } = useAsync((signal) => Api.load(signal));
  const [pageKey, setPageKey] = useState<string>('home');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [order, setOrder] = useState<string[]>([]);
  /* One section's wording at a time. The panel was every field on the page in
     one scroll, which is a list you search rather than a form you fill. */
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [seo, setSeo] = useState({ title: '', description: '', robots: '' });
  const [status, setStatus] = useState('');
  const [dirty, setDirty] = useState(false);

  const page: WebsitePage | undefined = useMemo(
    () => data?.pages.find((p) => p.page === pageKey) ?? data?.pages[0],
    [data, pageKey],
  );

  /* Load the chosen page into the editor. Anything typed and not saved is
     dropped on purpose — switching pages with unsaved text is what the guard
     below asks about. */
  useEffect(() => {
    if (!page) return;
    setDraft(Object.fromEntries(page.fields.filter((f) => f.value).map((f) => [f.key, f.value])));
    setHidden(new Set(page.hidden));
    setOrder(page.sections.map((s) => s.id));
    setOpenGroup(null);
    setSeo({ title: page.seo.title, description: page.seo.description, robots: page.seo.robots });
    setDirty(false);
  }, [page?.page]);          // eslint-disable-line react-hooks/exhaustive-deps


  /* Leaving with unsaved text is the one way this editor could lose work. */
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const edit = useCallback((key: string, value: string) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setDirty(true);
  }, []);

  const { frameRef, refresh, previewing } = usePreview({
    page: page?.page ?? 'home',
    fields: draft,
    hidden: [...hidden],
    order,
    fieldSpec: page?.fields ?? [],
    onEdit: edit,
  });

  async function save() {
    if (!page) return;
    setStatus('Saving…');
    try {
      await Api.save(page.page, {
        fields: draft,
        sections: Object.fromEntries((page.sections ?? []).map((s) => [s.id, hidden.has(s.id)])),
        order,
        seo,
      });
      setDirty(false);
      setStatus('Saved — live on the site now');
      await reload();
    } catch (e) { setStatus((e as Error).message); }
  }

  if (loading && !data) return <SkeletonPage />;
  if (error) return <div className="pg"><div className="err">Could not load the pages — {error.message}</div></div>;
  if (!data || !page) return null;

  const changed = Object.values(draft).filter(Boolean).length;
  const groups = [...new Set(page.fields.map((f) => f.group))];
  /* The saved order, with anything the console has not heard of kept where
     the page has it — a band added by a later build does not vanish from the
     panel because an older order was saved without it. */
  const byId = new Map(page.sections.map((s) => [s.id, s]));
  const ordered = [...order.filter((id) => byId.has(id)),
    ...page.sections.map((s) => s.id).filter((id) => !order.includes(id))]
    .map((id) => byId.get(id)!);

  function move(index: number, by: number) {
    const ids = ordered.map((s) => s.id);
    const to = index + by;
    if (to < 0 || to >= ids.length) return;
    const [moved] = ids.splice(index, 1);
    if (moved === undefined) return;
    ids.splice(to, 0, moved);
    setOrder(ids);
    setDirty(true);
  }

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Landing</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            {changed
              ? `${changed} of ${page.fields.length} pieces of wording are yours — the rest follow the design`
              : 'Every word on this page is the design’s. Click any highlighted text in the preview to make it yours.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: dirty ? 'var(--gold)' : 'var(--tx3)' }}>
            {dirty ? 'Unsaved changes' : status}
          </span>
          <a className="b bo" href={`${data.site_url}${routeOf(page.page)}`} target="_blank" rel="noreferrer">Open ↗</a>
          <button className="b bg" type="button" onClick={() => void save()} disabled={!dirty}>Save changes</button>
        </div>
      </div>

      {/* One page, no tabs: a row of chips with a single chip in it is a
          control that decides nothing. */}
      <div className="sg" style={{ marginBottom: 16, flexWrap: 'wrap' }} hidden={data.pages.length < 2}>
        {data.pages.map((p) => (
          <span key={p.page} {...(p.page === page.page ? { 'data-on': '' } : {})}
            onClick={() => {
              if (dirty && !confirm('You have unsaved changes on this page. Leave them?')) return;
              setPageKey(p.page);
            }}>{titleOf(p.page)}</span>
        ))}
      </div>

      <div className="pages-split" style={{ display: 'grid', gap: 18, alignItems: 'start' }}>
        <div className="crd" style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)', display: 'flex',
            alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontSize: 12, color: 'var(--tx3)' }}>
              Live preview · click highlighted text to edit it
            </span>
            <button className="b bo" style={{ padding: '5px 11px', fontSize: 11.5 }}
              type="button" onClick={() => void refresh()}>Refresh</button>
          </div>
          <iframe ref={frameRef} title="Page preview" style={{
            width: '100%', height: 620, border: 0, background: 'var(--ink)',
            opacity: previewing ? 0.5 : 1, transition: 'opacity .2s',
          }} />
        </div>

        <div style={{ display: 'grid', gap: 18 }}>
          {page.sections.length ? (
            <div className="crd" style={{ padding: '20px 22px' }}>
              <div className="kh" style={{ marginBottom: 6 }}>Sections on this page</div>
              <p className="hint" style={{ marginBottom: 12 }}>
                Top to bottom, as the page runs. A section switched off is removed from the page a
                visitor gets, not just hidden from view.
              </p>
              {ordered.map((s, i) => (
                <div key={s.id} style={{ display: 'flex', gap: 10, alignItems: 'center',
                  padding: '8px 0', borderBottom: '1px solid var(--line)' }}>
                  <input type="checkbox" checked={!hidden.has(s.id)}
                    aria-label={`Show ${s.label}`}
                    onChange={(e) => {
                      setHidden((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.delete(s.id); else next.add(s.id);
                        return next;
                      });
                      setDirty(true);
                    }} />
                  <div style={{ flex: 1, minWidth: 0, opacity: hidden.has(s.id) ? 0.5 : 1 }}>
                    {page.fields.some((f) => f.group === s.label) ? (
                      <button type="button" onClick={() => setOpenGroup(s.label)}
                        title="Open this section's wording"
                        style={{ background: 'none', border: 0, padding: 0, font: 'inherit',
                          color: 'inherit', cursor: 'pointer', fontSize: 13.5, textAlign: 'left' }}>
                        {s.label}
                      </button>
                    ) : <div style={{ fontSize: 13.5 }}>{s.label}</div>}
                    {s.when ? <div style={{ fontSize: 11, color: 'var(--tx3)' }}>Only with: {s.when}</div> : null}
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="b bo" type="button" aria-label={`Move ${s.label} up`}
                      style={{ padding: '3px 9px', fontSize: 12, lineHeight: 1.4 }}
                      disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                    <button className="b bo" type="button" aria-label={`Move ${s.label} down`}
                      style={{ padding: '3px 9px', fontSize: 12, lineHeight: 1.4 }}
                      disabled={i === ordered.length - 1} onClick={() => move(i, 1)}>↓</button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <div className="crd" style={{ padding: '20px 22px' }}>
            <div className="kh" style={{ marginBottom: 4 }}>Wording</div>
            <p className="hint" style={{ marginBottom: 12 }}>
              One line per section of the page. Open the one you want to change.
            </p>
            {groups.map((group) => {
              const mine = page.fields.filter((f) => f.group === group);
              const yours = mine.filter((f) => (draft[f.key] ?? '')).length;
              const band = page.sections.find((s) => s.label === group);
              const off = !!band && hidden.has(band.id);
              const open = openGroup === group;
              return (
                <div key={group} style={{ borderBottom: '1px solid var(--line)' }}>
                  <button type="button" onClick={() => setOpenGroup(open ? null : group)}
                    aria-expanded={open}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                      padding: '12px 2px', background: 'none', border: 0, color: 'inherit',
                      cursor: 'pointer', textAlign: 'left', font: 'inherit' }}>
                    <span style={{ width: 12, color: 'var(--tx3)', fontSize: 11 }}>{open ? '▾' : '▸'}</span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, opacity: off ? 0.5 : 1 }}>
                      {group}
                      {off ? <span style={{ color: 'var(--tx3)', fontSize: 11 }}> · hidden</span> : null}
                    </span>
                    <span style={{ fontSize: 11, color: yours ? 'var(--gold)' : 'var(--tx3)' }}>
                      {yours ? `${yours} of ${mine.length} yours` : `${mine.length} field${mine.length === 1 ? '' : 's'}`}
                    </span>
                  </button>
                  {open ? (
                    <div style={{ padding: '2px 0 14px' }}>
                      {off ? (
                        <p className="hint" style={{ marginBottom: 10 }}>
                          This section is switched off, so none of this is on the page.
                        </p>
                      ) : null}
                      {mine.map((f) => (
                        <FieldRow key={f.key} field={f} value={draft[f.key] ?? ''}
                          derived={data.derived[page.page]?.[f.key]}
                          onChange={(v) => edit(f.key, v)} />
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          <SeoPanel
            url={`${data.site_url}${routeOf(page.page)}`}
            seo={page.seo}
            value={seo}
            onChange={(next) => { setSeo(next); setDirty(true); }}
          />

        </div>
      </div>
    </div>
  );
}

function FieldRow({ field, value, derived, onChange }: {
  field: EditableField;
  value: string;
  derived?: string;
  onChange: (v: string) => void;
}) {
  /* A picture, not words: the value is a media id, so the row is an upload
     with the picture that is on the page right now beside it. What was just
     uploaded is held here, because the saved path only comes back with the
     next load and an empty box after an upload reads as a failed one. */
  if (field.kind === 'image') {
    return <ImageRow field={field} value={value} onChange={onChange} />;
  }

  /* An empty box does not mean the page says nothing — it means the page says
     what the design said, or what the database worked out. Showing that as
     the placeholder is the difference between "unfilled" and "not yours yet". */
  const placeholder = derived ?? field.fallback;
  return (
    <div className="fld">
      <label className="lbl">
        {field.label}
        {derived ? <span style={{ color: 'var(--goldtx)', marginLeft: 6, fontSize: 10 }}>counted</span> : null}
      </label>
      {field.multiline
        ? <textarea className="inp ta" rows={3} value={value} placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)} />
        : <input className="inp" value={value} placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)} />}
      {field.hint ? <p className="hint">{field.hint}</p> : null}
      {field.when ? <p className="hint" style={{ color: 'var(--tx3)' }}>Shown when: {field.when}</p> : null}
    </div>
  );
}

function ImageRow({ field, value, onChange }: {
  field: EditableField; value: string; onChange: (v: string) => void;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const path = picked ?? (value && value === field.value ? field.image_path ?? null : null);
  return (
    <div className="fld">
      <label className="lbl">{field.label}</label>
      <ImagePick path={path} hint={field.hint} onPick={(id, p) => {
        setPicked(p);
        onChange(id ? String(id) : '');
      }} />
    </div>
  );
}

const ROUTES: Record<string, string> = {
  home: '/', forex: '/forex-market/', contact: '/contact-us/', pricing: '/pricing/',
  broker: '/broker-partner/', learning: '/recorded-learning/',
  articles: '/indian-market/news-and-articles/', calls: '/indian-market/premium-trade-calls/',
  sotd: '/indian-market/stocks-of-the-day/', live: '/live-classes/batch-07/',
};
const routeOf = (page: string) => ROUTES[page] ?? '/';
const titleOf = (page: string) => ({
  home: 'Home', forex: 'Forex', contact: 'Contact', pricing: 'Pricing', broker: 'Broker',
  learning: 'Recorded learning', articles: 'Articles', calls: 'Trade calls',
  sotd: 'Stocks of the day', live: 'Live classes',
}[page] ?? page);
